import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { Shield, Key, CreditCard, Eye, EyeOff, CheckCircle2, AlertTriangle, Loader2, Lock, Trash2, Building2, Mail } from "lucide-react";

export default function PaymentSettings() {
  const [credentials, setCredentials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stripeKey, setStripeKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [showWebhook, setShowWebhook] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiClient.get("/admin/settings/payment-credentials");
      setCredentials(res.data);
    } catch {
      toast.error("Failed to load payment credentials status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSaveStripe = async () => {
    if (!stripeKey.trim() && !webhookSecret.trim()) { toast.error("Please enter a Stripe API key or webhook secret"); return; }
    setSaving(true);
    try {
      const payload = {};
      if (stripeKey.trim()) payload.stripe_api_key = stripeKey.trim();
      if (webhookSecret.trim()) payload.stripe_webhook_secret = webhookSecret.trim();
      const res = await apiClient.put("/admin/settings/stripe-key", payload);
      toast.success(res.data.message);
      setStripeKey("");
      setWebhookSecret("");
      setShowKey(false);
      setShowWebhook(false);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveStripe = async () => {
    if (!window.confirm("Remove the stored Stripe key? The system will fall back to the environment variable if set.")) return;
    setRemoving(true);
    try {
      const res = await apiClient.delete("/admin/settings/stripe-key");
      toast.success(res.data.message);
      fetchStatus();
    } catch {
      toast.error("Failed to remove Stripe key");
    } finally {
      setRemoving(false);
    }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-sag-navy border-t-transparent rounded-full" /></div></DashboardLayout>;

  const stripe = credentials?.stripe || {};
  const fiserv = credentials?.fiserv || {};
  const sendgrid = credentials?.sendgrid || {};

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl" data-testid="payment-settings">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="h-6 w-6 text-sag-navy" /> Payment & Integration Settings
          </h1>
          <p className="text-slate-500 mt-1">Securely manage payment processor credentials. Keys are encrypted and never exposed in code or GitHub.</p>
        </div>

        {/* Stripe */}
        <Card className="sag-card" data-testid="stripe-settings-card">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-indigo-100 text-indigo-600">
                  <CreditCard className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="font-heading text-lg">Stripe</CardTitle>
                  <CardDescription>Credit card payment processing (backup rail)</CardDescription>
                </div>
              </div>
              {stripe.configured ? (
                <Badge className="bg-emerald-100 text-emerald-700" data-testid="stripe-status-badge">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  {stripe.is_live_key ? "Live" : "Test Mode"}
                </Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-700" data-testid="stripe-status-badge">
                  <AlertTriangle className="h-3 w-3 mr-1" /> Not Configured
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {stripe.configured && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Current Key</span>
                  <span className="font-mono font-medium text-slate-700">{stripe.key_prefix}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Source</span>
                  <Badge variant="outline" className="text-xs capitalize">{stripe.source?.replace("_", " ")}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Mode</span>
                  <span className={`font-medium ${stripe.is_live_key ? "text-emerald-600" : "text-amber-600"}`}>
                    {stripe.is_live_key ? "Live (real charges)" : "Test (sandbox)"}
                  </span>
                </div>
                <Separator className="my-2" />
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Webhook Secret</span>
                  {stripe.webhook_configured ? (
                    <span className="font-mono font-medium text-emerald-600">{stripe.webhook_prefix}</span>
                  ) : (
                    <span className="text-amber-600 font-medium">Not configured</span>
                  )}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Webhook URL</span>
                  <span className="font-mono text-xs text-slate-600">{stripe.webhook_url}</span>
                </div>
              </div>
            )}

            <Separator />

            {/* API Key Input */}
            <div>
              <Label htmlFor="stripeKey" className="text-sm font-medium flex items-center gap-1">
                <Key className="h-3.5 w-3.5" /> {stripe.configured ? "Update Stripe Secret Key" : "Enter Stripe Secret Key"}
              </Label>
              <div className="flex items-center gap-2 mt-1.5">
                <div className="relative flex-1">
                  <Input
                    id="stripeKey"
                    type={showKey ? "text" : "password"}
                    value={stripeKey}
                    onChange={(e) => setStripeKey(e.target.value)}
                    placeholder="sk_live_... or sk_test_..."
                    className="pr-10 font-mono text-sm"
                    data-testid="stripe-key-input"
                  />
                  <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setShowKey(!showKey)}>
                    {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Webhook Secret Input */}
            <div>
              <Label htmlFor="webhookSecret" className="text-sm font-medium flex items-center gap-1">
                <Shield className="h-3.5 w-3.5" /> {stripe.webhook_configured ? "Update Webhook Signing Secret" : "Enter Webhook Signing Secret"}
              </Label>
              <p className="text-xs text-slate-400 mt-0.5 mb-1.5">Found in Stripe Dashboard → Webhooks → your endpoint → Signing secret</p>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Input
                    id="webhookSecret"
                    type={showWebhook ? "text" : "password"}
                    value={webhookSecret}
                    onChange={(e) => setWebhookSecret(e.target.value)}
                    placeholder="whsec_..."
                    className="pr-10 font-mono text-sm"
                    data-testid="stripe-webhook-input"
                  />
                  <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setShowWebhook(!showWebhook)}>
                    {showWebhook ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            <Button
              onClick={handleSaveStripe}
              disabled={saving || (!stripeKey.trim() && !webhookSecret.trim())}
              className="w-full bg-sag-navy hover:bg-sag-navy-800"
              data-testid="save-stripe-key-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
              {saving ? "Saving..." : "Save Securely"}
            </Button>

            <p className="text-xs text-slate-400 flex items-center gap-1">
              <Lock className="h-3 w-3" /> All credentials are encrypted before storage. Never committed to code or GitHub.
            </p>

            {stripe.configured && stripe.source === "admin_settings" && (
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={handleRemoveStripe}
                disabled={removing}
                data-testid="remove-stripe-key-btn"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                {removing ? "Removing..." : "Remove Stored Key"}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Fiserv / Scotiabank */}
        <Card className="sag-card" data-testid="fiserv-settings-card">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-blue-100 text-blue-600">
                  <Building2 className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="font-heading text-lg">Scotiabank / Fiserv IPG</CardTitle>
                  <CardDescription>Primary credit card processing (BSD currency)</CardDescription>
                </div>
              </div>
              {fiserv.configured ? (
                <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-3 w-3 mr-1" /> Configured</Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-700"><AlertTriangle className="h-3 w-3 mr-1" /> Partial</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Store ID</span>
                <span className={fiserv.store_id_set ? "text-emerald-600 font-medium" : "text-red-500"}>
                  {fiserv.store_id_set ? "Set (via environment)" : "Not configured"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Shared Secret (HMAC)</span>
                <span className={fiserv.shared_secret_set ? "text-emerald-600 font-medium" : "text-red-500"}>
                  {fiserv.shared_secret_set ? "Set (via environment)" : "Not configured"}
                </span>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3">Fiserv credentials are managed via environment variables (FISERV_STORE_ID, FISERV_SHARED_SECRET). Contact your deployment admin to update.</p>
          </CardContent>
        </Card>

        {/* SendGrid */}
        <Card className="sag-card" data-testid="sendgrid-settings-card">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-teal-100 text-teal-600">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="font-heading text-lg">SendGrid</CardTitle>
                  <CardDescription>Email delivery for invoices, reminders, and notifications</CardDescription>
                </div>
              </div>
              {sendgrid.configured ? (
                <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-3 w-3 mr-1" /> Configured</Badge>
              ) : (
                <Badge className="bg-red-100 text-red-700"><AlertTriangle className="h-3 w-3 mr-1" /> Not Set</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="bg-slate-50 rounded-lg p-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">API Key</span>
                <span className={sendgrid.configured ? "font-mono text-emerald-600 font-medium" : "text-red-500"}>
                  {sendgrid.key_prefix || "Not configured"}
                </span>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3">SendGrid key is managed via environment variable (SENDGRID_API_KEY). Contact your deployment admin to update.</p>
          </CardContent>
        </Card>

        {/* Security Note */}
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4 flex items-start gap-3">
            <Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">Security Best Practices</p>
              <ul className="space-y-1 text-blue-700 text-xs">
                <li>Stripe keys entered here are encrypted before database storage</li>
                <li>Keys are never committed to GitHub or exposed in source code</li>
                <li>Only admin users can view or update payment credentials</li>
                <li>Use <strong>sk_test_</strong> keys for testing, <strong>sk_live_</strong> for production</li>
                <li>Rotate keys immediately if you suspect they have been compromised</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
